import { useState } from "react"

export default function CreditPurchaseTable({data}) {
  const [expandedRows, setExpandedRows] = useState(new Set())

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (status, type) => {
    const baseClasses = "badge "
    if (type === "payment") {
      return status === "paid" ? baseClasses + "bg-success" : baseClasses + "bg-warning"
    } else {
      switch (status) {
        case "success":
          return baseClasses + "bg-success"
        case "pending":
          return baseClasses + "bg-warning"
        default:
          return baseClasses + "bg-secondary"
      }
    }
  }

  const MobileCardView = ({ record }) => (
    <div className="card mb-3">
      <div className="card-body">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                {record.institute_name ? (
                  <span className="badge bg-info mb-2">{record.institute_name}</span>
                ) : (
                  <span className="text-muted mb-2 d-block">-</span>
                )}
              </div>
              <h5 className="text-primary mb-0">{formatCurrency(record.amount)}</h5>
            </div>
          </div>
        </div>
        
        <div className="row mb-2">
          <div className="col-6">
            <small className="text-muted d-block">Payment Method</small>
            <span className="badge bg-secondary">{record.payment_method}</span>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Status</small>
            <span className={getStatusBadge(record.payment_status, "payment")}>
              {record.payment_status}
            </span>
          </div>
        </div>
        
        <div className="row mb-3">
          <div className="col-6">
            <small className="text-muted d-block">Purchase Date</small>
            <small className="text-dark">{formatDate(record.purchase_date)}</small>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Programs</small>
            <span className="badge bg-light text-dark">
              {record.programs.length} program{record.programs.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        
        {record.programs.length > 0 && (
          <div className="d-grid">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => toggleRow(record.id)}
              aria-label="Toggle programs"
            >
              {expandedRows.has(record.id) ? "Hide Details" : "View Details"}
            </button>
          </div>
        )}
        
        {expandedRows.has(record.id) && record.programs.length > 0 && (
          <div className="mt-3 pt-3 border-top">
            <h6 className="text-muted mb-3">
              <strong>Programs wise License</strong>
            </h6>
            {record.programs.map((program, index) => (
              <div key={program.id} className={`card mb-2 ${index === record.programs.length - 1 ? 'mb-0' : ''}`}>
                <div className="card-body py-2">
                  <div className="row align-items-center">
                    <div className="col-12 mb-2">
                      <small className="fw-medium">{program.program_title}</small>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Purchased</small>
                      <span className="badge bg-success">{program.credit_purchased}</span>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Balance</small>
                      <span className="badge bg-warning text-dark">{program.credit_balance}</span>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Amount</small>
                      <small className="fw-bold">{formatCurrency(program.total_amount)}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      <div className="d-none d-md-block">
        <div className="card blueborder">
          <div className="p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="">
                  <tr>
                    <th scope="col">Institute Name</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Payment Method</th>
                    <th scope="col">Payment Status</th>
                    <th scope="col">Purchase Date</th>
                    <th scope="col">Programs</th>
                    <th scope="col" style={{ width: "50px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((record) => (
                    <>
                      <tr key={record.id} className="align-middle">
                        <td>
                          {record.institute_name ? (
                            <span className="badge bg-info">{record.institute_name}</span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{formatCurrency(record.amount)}</td>
                        <td>
                          <span className="badge bg-secondary">{record.payment_method}</span>
                        </td>
                        <td>
                          <span className={getStatusBadge(record.payment_status, "payment")}>
                            {record.payment_status}
                          </span>
                        </td>
                        <td className="text-nowrap">{formatDate(record.purchase_date)}</td>
                        <td>
                          <span className="badge bg-light text-dark">
                            {record.programs.length} program{record.programs.length !== 1 ? "s" : ""}
                          </span>
                        </td>
                        <td>
                          {record.programs.length > 0 && (
                            <button
                              className="btn btn-sm btn-secondary border-0"
                              onClick={() => toggleRow(record.id)}
                              aria-label="Toggle programs"
                            >
                              Details
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedRows.has(record.id) && record.programs.length > 0 && (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="bg-light border-top">
                              <div className="p-3">
                                <h6 className="text-muted mb-3">
                                  <strong>Programs wise License</strong>
                                </h6>
                                <div className="table-responsive blueborder">
                                  <table className="table table-lg mb-0">
                                    <thead className="table-secondary">
                                      <tr>
                                        <th>Program Title</th>
                                        <th>Licenses Purchased</th>
                                        <th>Licenses Balance</th>
                                        <th>Total Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {record.programs.map((program) => (
                                        <tr key={program.id}>
                                          <td className="fw-medium">{program.program_title}</td>
                                          <td>
                                            <span className="badge bg-success">{program.credit_purchased}</span>
                                          </td>
                                          <td>
                                            <span className="badge bg-warning text-dark">
                                              {program.credit_balance}
                                            </span>
                                          </td>
                                          <td className="fw-bold">{formatCurrency(program.total_amount)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-light" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px" }}>
            <div className="row">
              <div className="col-md-6">
                <small className="text-muted">
                  Showing {data.length} license purchase record{data.length !== 1 ? "s" : ""}
                </small>
              </div>
              <div className="col-md-6 text-end">
                <small className="text-muted">
                  Total Programs: {data.reduce((sum, record) => sum + record.programs.length, 0)}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-block d-md-none">
        <div className="mb-3">
          {data.map((record) => (
            <MobileCardView key={record.id} record={record} />
          ))}
        </div>
      </div>
    </>
  )
}
